<template>
	<Teleport v-if="ctx.target" :to="ctx.target">
		<div class="aaremo-tour-footer">
			<!-- Unter md zeigt ButtonIcon nur das Symbol: Name per aria-label. -->
			<ButtonIcon
				color="primary"
				variant="outlined"
				:text="secondaryLabel"
				:aria-label="secondaryLabel"
				:icon="ctx.isFirst ? 'fa-solid fa-xmark' : 'fa-solid fa-arrow-left'"
				:disabled="isPreparing"
				data-test="tour-secondary-btn"
				@click="onSecondary"
			/>
			<v-btn
				color="primary"
				variant="flat"
				:append-icon="
					ctx.isLast ? 'fa-solid fa-check' : 'fa-solid fa-arrow-right'
				"
				:loading="isPreparing"
				:disabled="isPreparing"
				data-test="tour-primary-btn"
				@click="onPrimary"
			>
				{{
					ctx.isLast
						? t('component.tour.footer.done')
						: t('component.tour.footer.next')
				}}
			</v-btn>
		</div>
	</Teleport>
	<Teleport v-if="showLoading" to="body">
		<div class="aaremo-tour-loading" data-test="tour-loading" data-tour-ui>
			<v-progress-circular indeterminate color="primary" size="32" />
			<span>{{ t('component.tour.footer.wait') }}</span>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import ButtonIcon from '@/components/templates/Base/ButtonIcon.vue'
import { useTourStore } from '@/stores/system'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const { active, stepContext } = storeToRefs(useTourStore())
const ctx = computed(() => stepContext.value)
const secondaryLabel = computed(() =>
	ctx.value.isFirst
		? t('component.tour.footer.skip')
		: t('component.tour.footer.back')
)

// Show a "wird geladen" indicator if a step transition takes longer
// than ~250ms (e.g. the buildingDetail prepare hook fetches the
// buildings list + navigates). Without this the user sees only
// driver.js' dark overlay and may think the tour ended.
const showLoading = ref(false)
let loadingTimer: ReturnType<typeof setTimeout> | null = null

watch(
	[() => ctx.value.target, active],
	([target, isActive]) => {
		if (loadingTimer) {
			clearTimeout(loadingTimer)
			loadingTimer = null
		}
		if (isActive && !target) {
			loadingTimer = setTimeout(() => {
				showLoading.value = true
			}, 250)
		} else {
			showLoading.value = false
		}
	},
	{ immediate: true }
)

// Both nav callbacks are async (they await router.push + selector
// waits + API probes). Track the in-flight state so we can show the
// v-btn loading spinner and prevent double-clicks during the wait.
const isPreparing = ref(false)

async function onSecondary() {
	const c = ctx.value
	if (c.isFirst) {
		c.cancel()
		return
	}
	if (isPreparing.value) return
	isPreparing.value = true
	try {
		await c.back()
	} finally {
		isPreparing.value = false
	}
}

async function onPrimary() {
	const c = ctx.value
	if (c.isLast) {
		c.complete()
		return
	}
	if (isPreparing.value) return
	isPreparing.value = true
	try {
		await c.next()
	} finally {
		isPreparing.value = false
	}
}

function isEditableTarget(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false
	const tag = target.tagName
	if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
	if (target.isContentEditable) return true
	return false
}

function onKeydown(event: KeyboardEvent) {
	// Only react when a tour step is currently mounted (target is the
	// driver-popover footer slot). Ignore keystrokes typed into form fields.
	if (!ctx.value.target) return
	if (isEditableTarget(event.target)) return
	if (event.metaKey || event.ctrlKey || event.altKey) return

	switch (event.key) {
		case 'Enter':
		case 'ArrowRight':
			event.preventDefault()
			onPrimary()
			break
		case 'ArrowLeft':
			if (ctx.value.isFirst) return
			event.preventDefault()
			onSecondary()
			break
		case 'Escape':
			event.preventDefault()
			ctx.value.cancel()
			break
	}
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<style lang="scss">
.aaremo-tour-loading {
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
	padding: 20px 28px;
	background-color: rgb(var(--v-theme-surface));
	color: rgb(var(--v-theme-on-surface));
	border-radius: 8px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
	font-size: 14px;
	// Above driver.js' modal overlay and popover (z-index 1_000_000_000).
	z-index: 1000000001;
}
</style>
